import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock, Gauge, Globe2 } from "lucide-react";
import { MaskLines } from "@/components/common/Reveal";
import { site } from "@/config/site";

const pills = [
  { icon: ShieldCheck, label: "Security" },
  { icon: Lock, label: "Privacy" },
  { icon: Globe2, label: "Compliance" },
  { icon: Gauge, label: "Performance" },
];

export const Hero = () => (
  <section className="on-dark relative overflow-hidden bg-ink pb-16 pt-28 text-white sm:pt-32 lg:pb-24 lg:pt-40" data-testid="hero">
    <div className="absolute right-[-10%] top-[5%] h-[560px] w-[560px] rounded-full bg-teal/30 blur-[140px]" aria-hidden />
    <div className="container-x relative grid items-center gap-12 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-6">
        <p className="eyebrow mb-6 rounded-full bg-white/10 px-4 py-2 !text-teal-bright">Independent digital assurance</p>
        <MaskLines as="h1" testid="hero-title" className="text-[2.6rem] font-extrabold leading-[1.02] sm:text-6xl lg:text-[4.2rem]"
          lines={["Confidence", <>Through <span className="text-teal-bright">Rigor.</span></>]} />
        <p className="mt-6 text-xl font-bold text-white sm:text-2xl">{site.altHeadline}</p>
        <p className="lede mt-4 max-w-xl">{site.description}</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/request-assessment" className="btn-primary px-7 py-4 text-base" data-testid="hero-cta-primary">Request an Assessment <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/services" className="btn-ghost px-7 py-4 text-base" data-testid="hero-cta-secondary">Explore Our Services</Link>
        </div>
        <ul className="mt-10 flex flex-wrap gap-2.5">
          {pills.map(({ icon: I, label }) => (
            <li key={label} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200"><I className="h-4 w-4 text-teal-bright" aria-hidden />{label}</li>
          ))}
        </ul>
      </div>
      <motion.div className="relative lg:col-span-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
        <div className="overflow-hidden rounded-2xl border border-white/15 shadow-[0_40px_90px_-30px_rgba(0,0,0,.7)]">
          <img src="/img/hero.webp" alt="Security analyst reviewing assessment results on multiple monitors" width="1400" height="934" className="aspect-[4/3] w-full object-cover" fetchPriority="high" decoding="async" />
        </div>
        <div className="absolute -bottom-5 left-4 right-4 rounded-xl bg-white p-4 text-ink shadow-xl sm:left-auto sm:right-6 sm:w-80" data-testid="hero-readiness-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Launch readiness</p>
          <p className="mt-1 text-lg font-extrabold">Ready with conditions</p>
          <div className="mt-3 h-2 rounded-full bg-slate-100"><div className="h-2 w-[72%] rounded-full bg-teal" /></div>
          <p className="mt-2 text-xs text-slate-600">72% of findings remediated · 2 blockers open</p>
        </div>
      </motion.div>
    </div>
  </section>
);
